import type { ColDef } from "ag-grid-community";

export const getPathColumnDefinitions = (
  _onToggleStatus: (id: string, currentStatus: string) => void,
  _loadingPaths: Set<string>
): ColDef[] => [
  {
    headerName: "Path ID",
    field: "id",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 180,
    valueFormatter: (params: any) => {
      return params.value || "No ID";
    }
  },
  {
    headerName: "Path",
    field: "path",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 200,
    maxWidth: 350,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "No Path";
      }
      return params.value;
    }
  },
  {
    headerName: "Repository",
    field: "githubRepo",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 200,
    maxWidth: 350,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "No Repository";
      }
      return params.value;
    }
  },
  {
    headerName: "Description",
    field: "description",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 200,
    maxWidth: 400,
    wrapText: true,
    autoHeight: true,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "No Description";
      }
      return params.value;
    }
  },
  {
    headerName: "Subfolder",
    field: "githubSubfolder",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 200,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "Root";
      }
      return `/${params.value}`;
    }
  },
  {
    headerName: "Branch/Tag",
    field: "githubTag",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 200,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "No Branch/Tag";
      }
      const tag = params.value;
      const isMain = tag === "main" || tag === "master";
      const isVersion = tag.startsWith("v");
      
      if (isMain) {
        return `${tag} (default)`;
      } else if (isVersion) {
        return `${tag} (release)`;
      }
      return tag;
    }
  },
  {
    headerName: "Status",
    field: "status",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 150,
    valueFormatter: (params: any) => {
      const status = params.value || "unknown";
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  },
  {
    headerName: "Traffic",
    field: "dailyTraffic",
    filter: "agNumberColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 180,
    valueFormatter: (params: any) => {
      const count = params.value || 0;
      if (count > 10000) {
        return `${(count / 1000).toFixed(1)}k visits/day`;
      }
      return `${count} visits/day`;
    }
  },
  {
    headerName: "Conversion",
    field: "conversionRate",
    filter: "agNumberColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 120,
    maxWidth: 150,
    valueFormatter: (params: any) => {
      const rate = params.value || 0;
      return `${(rate * 100).toFixed(1)}%`;
    }
  },
  {
    headerName: "Last Deployed",
    field: "lastDeployed",
    filter: "agDateColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 150,
    maxWidth: 200,
    valueFormatter: (params: any) => {
      if (!params.value) {
        return "Never";
      }
      
      const date = new Date(params.value);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) {
        return "Today";
      } else if (diffInDays === 1) {
        return "Yesterday";
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else if (diffInDays < 30) {
        const weeks = Math.floor(diffInDays / 7);
        return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
      } else if (diffInDays < 365) {
        const months = Math.floor(diffInDays / 30);
        return `${months} ${months === 1 ? "month" : "months"} ago`;
      } else {
        const years = Math.floor(diffInDays / 365);
        return `${years} ${years === 1 ? "year" : "years"} ago`;
      }
    }
  },
  {
    headerName: "Deploy Status",
    field: "deployStatus",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 150,
    maxWidth: 200,
    valueFormatter: (params: any) => {
      const deployStatus = params.value || "unknown";
      const statusText = {
        success: "✓ Deployed",
        pending: "⏳ Pending",
        failed: "✗ Failed",
        building: "🔨 Building",
        unknown: "Unknown"
      };
      return statusText[deployStatus as keyof typeof statusText] || deployStatus;
    }
  },
  {
    headerName: "Cache Status",
    field: "cacheStatus",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 140,
    maxWidth: 180,
    valueFormatter: (params: any) => {
      const cacheStatus = params.value || "unknown";
      const statusText = {
        hot: "🔥 Hot",
        warm: "♨️ Warm",
        cold: "❄️ Cold",
        bypass: "⏭️ Bypass",
        unknown: "Unknown"
      };
      return statusText[cacheStatus as keyof typeof statusText] || cacheStatus;
    }
  },
  {
    headerName: "External Browser",
    field: "forceExternalBrowser",
    filter: "agTextColumnFilter",
    sortable: true,
    resizable: true,
    minWidth: 140,
    maxWidth: 180,
    valueFormatter: (params: any) => {
      return params.value ? "Enabled" : "Disabled";
    }
  }
];

// Export default column definitions
export const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 100,
  suppressSizeToFit: true,
  autoHeaderHeight: true
};